import { Component, type ReactNode } from "react";
import { AlertTriangle, Home, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PortalHeader } from "@/components/portal/PortalHeader";
import { PortalFooter } from "@/components/portal/PortalFooter";

type BoundaryContext = "site" | "admin" | "portal" | "partner" | "root";

type Props = {
  children: ReactNode;
  context?: BoundaryContext;
  resetKey?: string;
};

type State = {
  error: Error | null;
  eventId?: string;
};

const contextCopy: Record<BoundaryContext, { title: string; body: string; homeHref: string; homeLabel: string }> = {
  site: {
    title: "Something went wrong",
    body: "The page hit an unexpected error. We have logged it and you can retry or go back to the homepage.",
    homeHref: "/",
    homeLabel: "Homepage",
  },
  admin: {
    title: "Something went wrong",
    body: "This admin page hit an unexpected error. The issue has been reported so we can investigate it.",
    homeHref: "/admin/overview",
    homeLabel: "Admin overview",
  },
  portal: {
    title: "Something went wrong",
    body: "This portal page hit an unexpected error. The issue has been reported and your application data is safe.",
    homeHref: "/portal/dashboard",
    homeLabel: "Portal dashboard",
  },
  partner: {
    title: "Something went wrong",
    body: "This partner page hit an unexpected error. The issue has been reported and your clients' data is safe.",
    homeHref: "/partner/dashboard",
    homeLabel: "Partner dashboard",
  },
  root: {
    title: "Something went wrong",
    body: "The app hit an unexpected error. We have logged it and you can retry from here.",
    homeHref: "/",
    homeLabel: "Homepage",
  },
};

function ErrorCard({ context, eventId }: { context: BoundaryContext; eventId?: string }) {
  const copy = contextCopy[context];

  return (
    <div className="min-h-[420px] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg rounded-lg border border-border bg-card p-6 text-center shadow-sm">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
          <AlertTriangle className="h-6 w-6" />
        </div>
        <h1 className="text-2xl font-display font-bold text-foreground">{copy.title}</h1>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{copy.body}</p>
        {eventId && (
          <p className="mt-3 text-xs text-muted-foreground">
            Error ID: <span className="font-mono">{eventId}</span>
          </p>
        )}
        <div className="mt-6 flex flex-col justify-center gap-2 sm:flex-row">
          <Button onClick={() => window.location.reload()} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Reload
          </Button>
          <Button variant="outline" asChild className="gap-2">
            <a href={copy.homeHref}>
              <Home className="h-4 w-4" />
              {copy.homeLabel}
            </a>
          </Button>
        </div>
      </div>
    </div>
  );
}

function ErrorFallback({ context, eventId }: { context: BoundaryContext; eventId?: string }) {
  if (context === "portal") {
    return (
      <div className="min-h-screen bg-background pt-14 flex flex-col">
        <PortalHeader backTo="/portal/dashboard" backLabel="Dashboard" />
        <main className="flex-1">
          <ErrorCard context={context} eventId={eventId} />
        </main>
        <PortalFooter />
      </div>
    );
  }

  return <ErrorCard context={context} eventId={eventId} />;
}

export default class AppErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack?: string }) {
    const context = this.props.context || "root";

    import("@/lib/sentry")
      .then(({ initSentry, Sentry }) => {
        const sentryWithClient = Sentry as typeof Sentry & { getClient?: () => unknown };
        if (!sentryWithClient.getClient?.()) {
          initSentry();
        }

        const eventId = Sentry.captureException(error, {
          tags: { error_boundary: context },
          extra: {
            componentStack: info.componentStack,
            path: window.location.pathname,
            url: window.location.href,
          },
        });

        this.setState({ eventId });
      })
      .catch(() => {
        // Never let telemetry failure break the fallback UI.
      });
  }

  componentDidUpdate(prevProps: Props) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null, eventId: undefined });
    }
  }

  render() {
    if (this.state.error) {
      return <ErrorFallback context={this.props.context || "root"} eventId={this.state.eventId} />;
    }

    return this.props.children;
  }
}
